import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { ENTRY_STAGES } from "@/engine/diagnostics/entryTiming";
import { Button } from "@/components/ui/button";
import { cn } from "@/engine/support/classNames";

import { useEntryWait } from "./useEntryWait";

/**
 * What the entry card is waiting for, named.
 *
 * The walk in is three waits that look identical from outside — the session
 * check, the channel, the first audio. A spinner says one thing about all three:
 * that something is happening. `entryTiming` already stamps each as it passes
 * and every one already has a translated name, so the card can say which one it
 * is standing on instead.
 *
 * No milliseconds. The same numbers reach analytics through
 * `room-entry-stage-recorded`, and beside a step on a visitor's screen they are
 * a figure nobody can act on. `ConnectingStatus` keeps them, because a
 * diagnostic pill inside the room is where they are apt.
 */
export interface EntryStepsProps {
  /**
   * Abandons the walk in. Offered only once the wait has gone on long enough
   * to be worth abandoning — absent where there is nowhere to go back to.
   */
  onCancel?: () => void;
}

export function EntrySteps({ onCancel }: EntryStepsProps) {
  const { t } = useTranslation();
  const { marks, current, elapsed, counting, slow } = useEntryWait();

  return (
    <div
      data-testid="entry-steps"
      className="flex flex-col items-center gap-3 py-6"
    >
      <ol className="flex w-full max-w-[15rem] flex-col gap-2">
        {ENTRY_STAGES.map((stage) => {
          const done = marks[stage] !== undefined;
          const active = stage === current;
          return (
            <li
              key={stage}
              data-testid={`entry-step-${stage}`}
              data-state={done ? "done" : active ? "active" : "pending"}
              className={cn(
                "flex items-center gap-2 text-sm",
                done
                  ? "text-muted-foreground"
                  : active
                    ? "text-foreground"
                    : "text-muted-foreground/50",
              )}
            >
              {/* A fixed box for the mark, so naming the next step does not
                  shift the words of the ones already passed. */}
              <span className="inline-flex w-4 justify-center">
                {done ? (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                ) : active ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                ) : null}
              </span>
              {t(`entryStage.${stage}`)}
            </li>
          );
        })}
      </ol>

      {/* Reserved whether or not it is counting, so the card does not grow a
          line a second after the panel appeared. */}
      <p
        data-testid="entry-steps-elapsed"
        aria-hidden={!counting}
        className={cn(
          "text-xs tabular-nums text-muted-foreground transition-opacity",
          counting ? "opacity-70" : "opacity-0",
        )}
      >
        {counting ? (
          <>
            {(elapsed / 1000).toFixed(1)}
            {t("diagnostics.seconds")}
          </>
        ) : (
          " "
        )}
      </p>

      {/* The warning and the way out arrive together, because the way out is
          only worth having once there is something to be waiting on. Before
          that it is a control everybody reads past. */}
      {slow && (
        <div
          data-testid="entry-steps-slow"
          role="status"
          className="flex flex-col items-center gap-2"
        >
          <p className="max-w-[18rem] text-center text-xs text-muted-foreground">
            {t("entry.slowNetwork")}
          </p>
          {onCancel && (
            <Button
              data-testid="entry-cancel"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8 text-xs text-muted-foreground"
            >
              {t("entry.cancelEntry")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
